import { Check, Circle, Info } from 'lucide-react'

export type FilaNoua = 'prezentare' | 'marfa' | 'bani' | 'teren' | 'fisiere' | 'activitate'

/** Un pas din `GET /campanii/:id/summary` — vezi `apps/api/src/modules/campanii/pasi.ts`. */
export interface Pas {
  cheie: string
  stare: 'facut' | 'acum' | 'urmeaza' | 'info'
  ton?: 'rosu' | 'galben'
  text: string
  detaliu?: string
  fila?: FilaNoua
  cerere?: number
}

const ETICHETA_FILA: Record<FilaNoua, string> = {
  prezentare: 'Prezentare',
  marfa: 'Marfă',
  bani: 'Bani',
  teren: 'Pe teren',
  fisiere: 'Fișiere',
  activitate: 'Activitate',
}

/**
 * „Ce urmeaza" — pasii activitatii, cititi din date (nu bifati de mana). Ce e de facut acum sta
 * in culoare; ce s-a facut e gri, bifat; ce vine e un cerc gol.
 */
export function CeUrmeaza({
  pasi,
  onDeschide,
  poateAproba,
  onInchideActivitatea,
}: {
  pasi: Pas[]
  onDeschide: (fila: FilaNoua, cerere?: number) => void
  /** Doar adminii aproba cereri; ceilalti doar o vad. */
  poateAproba: boolean
  /** Pasul „Închide activitatea": deschide fereastra cu raportul (doar cine poate scrie). */
  onInchideActivitatea?: () => void
}) {
  if (!pasi.length)
    return <p className="text-mic text-b4l-text-muted">Nimic de urmărit aici, deocamdată.</p>
  return (
    <ol className="grid">
      {pasi.map((p) => {
        const acum = p.stare === 'acum'
        const ton =
          p.ton === 'rosu'
            ? 'bg-b4l-red-light text-b4l-red-dark'
            : p.ton === 'galben'
              ? 'bg-b4l-amber-light text-b4l-amber-dark'
              : 'bg-b4l-blue-light text-b4l-blue-dark'
        return (
          <li
            key={p.cheie}
            className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 border-t border-b4l-border py-2.5 first:border-t-0"
          >
            <span
              aria-hidden
              className={`grid h-6 w-6 place-items-center rounded-full ${
                p.stare === 'facut' ? 'bg-b4l-green text-white' : acum ? ton : 'text-b4l-text-dim'
              }`}
            >
              {p.stare === 'facut' ? (
                <Check size={14} />
              ) : p.stare === 'info' ? (
                <Info size={15} />
              ) : (
                <Circle size={acum ? 8 : 14} fill={acum ? 'currentColor' : 'none'} />
              )}
            </span>
            <span className="grid min-w-0 gap-px">
              <span
                className={`text-corp ${
                  p.stare === 'facut' ? 'text-b4l-text-muted' : 'font-medium text-b4l-text'
                }`}
              >
                <span className="sr-only">
                  {p.stare === 'facut'
                    ? 'Făcut: '
                    : acum
                      ? 'De făcut acum: '
                      : p.stare === 'urmeaza'
                        ? 'Urmează: '
                        : ''}
                </span>
                {p.text}
              </span>
              {p.detaliu && <span className="text-mic text-b4l-text-dim">{p.detaliu}</span>}
            </span>
            {p.cheie === 'inchidere' && acum && onInchideActivitatea && (
              <button
                type="button"
                onClick={onInchideActivitatea}
                className="whitespace-nowrap rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1 text-mic font-semibold hover:border-b4l-blue"
              >
                Închide cu raport
              </button>
            )}
            {p.fila && p.fila !== 'prezentare' && (
              <button
                type="button"
                onClick={() => onDeschide(p.fila!, p.cerere)}
                className={`whitespace-nowrap rounded-caseta px-2.5 py-1 text-mic ${
                  acum
                    ? 'border border-b4l-border bg-b4l-surface font-semibold hover:border-b4l-blue'
                    : 'text-b4l-text-muted hover:bg-b4l-bg'
                }`}
                aria-label={`${p.text} — deschide ${ETICHETA_FILA[p.fila]}`}
              >
                {p.cerere && acum
                  ? poateAproba && p.ton === 'rosu'
                    ? 'Verifică'
                    : 'Vezi cererea'
                  : ETICHETA_FILA[p.fila]}
              </button>
            )}
          </li>
        )
      })}
    </ol>
  )
}
